import { Component, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Role, SetupWizardLocation, TextUtils } from '@mira-avant/mtlibs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
	BEFORE_AFTER_STATUS_CHANGED_COLOR,
	BEFORE_AFTER_STATUS_NEW_COLOR,
	BEFORE_AFTER_STATUS_REMOVED_COLOR,
	BEFORE_AFTER_STATUS_UNCHANGED_COLOR,
	BEFORE_AFTER_STATUS_UPSERT_COLOR,
} from '../../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SetupWizard } from '../../../../../../../../services/setup-wizard';
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
	selector: 'app-setup-wizard-location-users-status',
	imports: [NzTagModule, NzTooltipModule, NzSpinModule, NzIconModule, NzButtonModule],
	standalone: true,
	templateUrl: './setup-wizard-location-users-status.html',
	styleUrl: './setup-wizard-location-users-status.scss',
})
export class SetupWizardLocationUsersStatus {
	enableEditButton = input<boolean>(true);
	location = input<SetupWizardLocation>();
	existingSchoolAdmins = signal<number>(0);
	addedSchoolAdmins = signal<number>(0);
	removedSchoolAdmins = signal<number>(0);
	loading = signal<boolean>(true);
	onEdit = output<SetupWizardLocation>();
	private sub?: Subscription;
	public BEFORE_AFTER_STATUS_UPSERT_COLOR = BEFORE_AFTER_STATUS_UPSERT_COLOR;
	public BEFORE_AFTER_STATUS_NEW_COLOR = BEFORE_AFTER_STATUS_NEW_COLOR;
	public BEFORE_AFTER_STATUS_CHANGED_COLOR = BEFORE_AFTER_STATUS_CHANGED_COLOR;
	public BEFORE_AFTER_STATUS_UNCHANGED_COLOR = BEFORE_AFTER_STATUS_UNCHANGED_COLOR;
	public BEFORE_AFTER_STATUS_REMOVED_COLOR = BEFORE_AFTER_STATUS_REMOVED_COLOR;

	constructor(private setupWizard: SetupWizard) {
		const location$ = toObservable(this.location).pipe(distinctUntilChanged());

		this.sub = combineLatest([this.setupWizard.preview$, location$]).subscribe(([preview, location]) => {
			if (!location) {
				this.resetCounts();
				return;
			}

			const locationBeforeAfter = location.after ?? location.before;

			if (!locationBeforeAfter?.location_uuid) {
				this.resetCounts();
				return;
			}

			let existingSchoolAdmins = 0;
			let addedSchoolAdmins = 0;
			let removedSchoolAdmins = 0;

			for (const link of preview.payload.schoolAdminsToLocations) {
				const beforeAfter = link.after ?? link.before;

				if (!beforeAfter) {
					throw new Error('Link before/after data is missing');
				}

				if (
					TextUtils.isNonEmptyString(beforeAfter.location_uuid) &&
					beforeAfter.location_uuid !== locationBeforeAfter.location_uuid
				) {
					continue;
				}

				// Find the user associated with the link in the preview.payload.users
				// area by user_uuid
				const user = preview.payload.users.find(u => {
					const userBeforeAfter = u.after ?? u.before;

					if (!userBeforeAfter) {
						throw new Error('User before/after data is missing');
					}

					// Locate user by matching user-uuid
					return (
						(TextUtils.isNonEmptyString(userBeforeAfter.user_uuid) &&
							userBeforeAfter.user_uuid === beforeAfter.user_uuid) ||
						// or by matching school_id_or_email if it exists
						(TextUtils.isNonEmptyString(userBeforeAfter.school_id_or_email) &&
							userBeforeAfter.school_id_or_email === beforeAfter.school_id_or_email)
					);
				});

				if (!user) {
					continue;
				}

				const userBeforeAfter = user.after ?? user.before;

				if (!userBeforeAfter) {
					continue;
				}

				if (!link.before && link.after && link.after.active) {
					if (userBeforeAfter.group === Role.SCHOOL_ADMIN) {
						addedSchoolAdmins++;
					}
				} else if (link.before && link.before.active && !link.after) {
					if (userBeforeAfter.group === Role.SCHOOL_ADMIN) {
						existingSchoolAdmins++;
					}
				} else if (link.before && link.after && link.before.active && !link.after.active) {
					if (userBeforeAfter.group === Role.SCHOOL_ADMIN) {
						removedSchoolAdmins++;
					}
				}
			}

			this.existingSchoolAdmins.set(existingSchoolAdmins);
			this.addedSchoolAdmins.set(addedSchoolAdmins);
			this.removedSchoolAdmins.set(removedSchoolAdmins);

			this.loading.set(false);
		});
	}

	editLocation(location: SetupWizardLocation) {
		this.onEdit.emit(location);
	}

	private resetCounts() {
		this.existingSchoolAdmins.set(0);
		this.addedSchoolAdmins.set(0);
		this.removedSchoolAdmins.set(0);
		this.loading.set(false);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
